import {
  ChevronDown,
  Paperclip,
  ReceiptEuro,
  ShoppingCart,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { InvoiceType } from "@/lib/constants/invoice-type";
import { cn } from "@/lib/utils";

export type InvoiceTypeOption = InvoiceType | "no_booking";

type Props = {
  currentType: InvoiceType;
  isBooking: boolean;
  onSelect: (option: InvoiceTypeOption) => void;
  disabled?: boolean;
};

export const InvoiceTypeDropdown = ({
  currentType,
  isBooking,
  onSelect,
  disabled = false,
}: Props) => {
  const { t } = useTranslation();

  const currentOption: InvoiceTypeOption = isBooking
    ? currentType
    : "no_booking";

  const handleSelectType = (option: InvoiceTypeOption) => {
    if (option === currentOption) return;
    onSelect(option);
  };

  const getIcon = (option: InvoiceTypeOption) => {
    switch (option) {
      case InvoiceType.PAYABLE:
        return <ShoppingCart className="h-4 w-4" />;
      case InvoiceType.RECEIVABLE:
        return <ReceiptEuro className="h-4 w-4" />;
      case "no_booking":
        return <Paperclip className="h-4 w-4" />;
    }
  };

  const getLabel = (option: InvoiceTypeOption): string => {
    switch (option) {
      case InvoiceType.PAYABLE:
        return t("invoice_type_dropdown.payable");
      case InvoiceType.RECEIVABLE:
        return t("invoice_type_dropdown.receivable");
      case "no_booking":
        return t("invoice_type_dropdown.no_booking");
    }
  };

  const getDescription = (option: InvoiceTypeOption): string | null => {
    switch (option) {
      case "no_booking":
        return t("invoice_type_dropdown.no_booking_description");
      default:
        return null;
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          disabled={disabled}
        >
          {getIcon(currentOption)}
          {getLabel(currentOption)}
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          {t("invoice_type_dropdown.edit_type")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="space-y-1">
          {[
            InvoiceType.PAYABLE,
            InvoiceType.RECEIVABLE,
            "no_booking" as const,
          ].map((option) => (
            <DropdownMenuItem
              key={option}
              className={cn(
                "gap-2 cursor-pointer",
                currentOption === option && "bg-muted",
              )}
              onSelect={() => handleSelectType(option)}
            >
              <div className="flex items-start gap-2 flex-1">
                <span className="mt-0.5">{getIcon(option)}</span>
                <div className="flex flex-col">
                  <span>{getLabel(option)}</span>
                  {getDescription(option) && (
                    <span className="text-xs text-muted-foreground">
                      {getDescription(option)}
                    </span>
                  )}
                </div>
              </div>
              {currentOption === option && (
                <span className="text-primary">✓</span>
              )}
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
